import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import CreateDealModal from '../components/CreateDealModal';

const Pipeline = () => {
  const [pipeline, setPipeline] = useState({});
  const [totals, setTotals] = useState({});
  const [stages, setStages] = useState({});
  const [products, setProducts] = useState([]);
  const [fixedCosts, setFixedCosts] = useState(['Creative Production', 'Misc Fees', 'Travel Fees']);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [draggedDeal, setDraggedDeal] = useState(null);
  const [viewMode, setViewMode] = useState('kanban');
  const [allDeals, setAllDeals] = useState([]);
  const CURRENT_YEAR = new Date().getFullYear();
  // Year selector value: a number for a specific year, or the string 'all' for All Time
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [quarterlyWeighted, setQuarterlyWeighted] = useState(true);

  // Sales Target modal
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [goals, setGoals] = useState([]);
  const [goalsForm, setGoalsForm] = useState({ q1: '', q2: '', q3: '', q4: '', annual: '' });
  const [goalYear, setGoalYear] = useState(CURRENT_YEAR);
  const [savingGoals, setSavingGoals] = useState(false);

  // Historical deals
  const [historicals, setHistoricals] = useState([]);
  const [showHistoricalModal, setShowHistoricalModal] = useState(false);
  const [histForm, setHistForm] = useState({ name: '', revenue: '', expenses: '', year: String(CURRENT_YEAR - 1), quarter: '1' });
  const [savingHistorical, setSavingHistorical] = useState(false);

  // Grab-to-scroll for kanban
  const kanbanRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const onMouseDown = useCallback((e) => {
    // Don't grab if clicking a deal card or button
    if (e.target.closest('a') || e.target.closest('button') || e.target.closest('[draggable]')) return;
    const el = kanbanRef.current;
    if (!el) return;
    isDragging.current = true;
    startX.current = e.pageX - el.offsetLeft;
    scrollLeft.current = el.scrollLeft;
    el.style.cursor = 'grabbing';
    el.style.userSelect = 'none';
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const el = kanbanRef.current;
    if (!el) return;
    const x = e.pageX - el.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    el.scrollLeft = scrollLeft.current - walk;
  }, []);

  const onMouseUp = useCallback(() => {
    isDragging.current = false;
    const el = kanbanRef.current;
    if (el) {
      el.style.cursor = 'grab';
      el.style.removeProperty('user-select');
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('mousemove', onMouseMove);
    return () => {
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousemove', onMouseMove);
    };
  }, [onMouseUp, onMouseMove]);


  const verticals = [{ id: 'gaming', label: 'Gaming' }, { id: 'entertainment', label: 'Entertainment' }, { id: 'cpg', label: 'CPG' }, { id: 'foodbev', label: 'Food & Bev' }, { id: 'qsr', label: 'QSR' }, { id: 'insurance', label: 'Insurance' }, { id: 'finance', label: 'Finance' }, { id: 'auto', label: 'Auto' }, { id: 'retail', label: 'Retail' }, { id: 'sports', label: 'Sports' }, { id: 'tech', label: 'Tech' }, { id: 'telco', label: 'Telco' }, { id: 'other', label: 'Other' }];
  const stageOrder = ['prospecting', 'rfp_received', 'rfp_submitted', 'client_recommended', 'verbal', 'io_sent', 'closed_won', 'closed_lost'];

  useEffect(() => { fetchPipeline(); fetchAllDeals(); fetchGoals(); fetchHistoricals(); }, []);

  const fetchPipeline = async () => {
    try { const res = await api.get('/pipeline'); setPipeline(res.data.pipeline); setTotals(res.data.totals); setStages(res.data.stages); setProducts(res.data.products); if (res.data.fixedCosts) setFixedCosts(res.data.fixedCosts); } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  const fetchAllDeals = async () => { try { const res = await api.get('/deals'); setAllDeals(res.data.deals || []); } catch (e) {} };

  const fetchGoals = async () => {
    try {
      // Fetch all years; we'll filter client-side per selected year
      const res = await api.get('/autopilot/goals');
      const list = res.data.goals || [];
      setGoals(list);
    } catch (e) { console.error('Goals fetch failed:', e); }
  };

  const fetchHistoricals = async () => {
    try {
      const res = await api.get('/autopilot/historical-deals');
      setHistoricals(res.data.historical_deals || []);
    } catch (e) { console.error('Historicals fetch failed:', e); }
  };

  const openTargetModal = () => {
    // Default modal to the currently-viewed year (unless All Time, then current year)
    const targetYear = (selectedYear === 'all') ? CURRENT_YEAR : selectedYear;
    const form = { q1: '', q2: '', q3: '', q4: '', annual: '' };
    goals.forEach(g => {
      if (g.year === targetYear && form.hasOwnProperty(g.period)) {
        form[g.period] = g.target_amount ? String(g.target_amount) : '';
      }
    });
    setGoalYear(targetYear);
    setGoalsForm(form);
    setShowTargetModal(true);
  };

  const saveGoals = async () => {
    setSavingGoals(true);
    try {
      const yr = goalYear;
      const payload = [
        { period: 'q1',     label: `Q1 ${yr}`,     target_amount: parseInt(String(goalsForm.q1).replace(/[^0-9]/g, '')) || 0 },
        { period: 'q2',     label: `Q2 ${yr}`,     target_amount: parseInt(String(goalsForm.q2).replace(/[^0-9]/g, '')) || 0 },
        { period: 'q3',     label: `Q3 ${yr}`,     target_amount: parseInt(String(goalsForm.q3).replace(/[^0-9]/g, '')) || 0 },
        { period: 'q4',     label: `Q4 ${yr}`,     target_amount: parseInt(String(goalsForm.q4).replace(/[^0-9]/g, '')) || 0 },
        { period: 'annual', label: `Annual ${yr}`, target_amount: parseInt(String(goalsForm.annual).replace(/[^0-9]/g, '')) || 0 },
      ].filter(g => g.target_amount > 0);
      await api.put('/autopilot/goals', { year: yr, goals: payload });
      await fetchGoals();
      setShowTargetModal(false);
    } catch (e) { alert('Failed to save goals'); } finally { setSavingGoals(false); }
  };

  const saveHistorical = async () => {
    const name = (histForm.name || '').trim();
    const revenue = parseInt(String(histForm.revenue).replace(/[^0-9]/g, '')) || 0;
    const expenses = parseInt(String(histForm.expenses).replace(/[^0-9]/g, '')) || 0;
    const year = parseInt(histForm.year);
    const quarter = parseInt(histForm.quarter);
    if (!name) { alert('Deal name required'); return; }
    if (!year) { alert('Year required'); return; }
    if (!(quarter >= 1 && quarter <= 4)) { alert('Quarter required'); return; }

    setSavingHistorical(true);
    try {
      await api.post('/autopilot/historical-deals', { name, revenue, expenses, year, quarter });
      await fetchHistoricals();
      setShowHistoricalModal(false);
      setHistForm({ name: '', revenue: '', expenses: '', year: String(CURRENT_YEAR - 1), quarter: '1' });
    } catch (e) {
      alert('Failed to save historical deal');
    } finally {
      setSavingHistorical(false);
    }
  };

  const deleteHistorical = async (id) => {
    if (!window.confirm('Delete this historical deal?')) return;
    try {
      await api.delete(`/autopilot/historical-deals/${id}`);
      fetchHistoricals();
    } catch (e) {
      alert('Failed to delete');
    }
  };

  const fmtGoalNum = (val) => { const num = parseInt(String(val).replace(/[^0-9]/g, '')); return num ? num.toLocaleString() : ''; };

  const handleAnnualGoalChange = (val) => {
    setGoalsForm(prev => {
      const annual = parseInt(val.replace(/[^0-9]/g, '')) || 0;
      const allEmpty = !prev.q1 && !prev.q2 && !prev.q3 && !prev.q4;
      if (allEmpty && annual > 0) { const q = Math.round(annual / 4); return { q1: String(q), q2: String(q), q3: String(q), q4: String(q), annual: val }; }
      return { ...prev, annual: val };
    });
  };

  const handleQuarterGoalChange = (quarter, val) => {
    setGoalsForm(prev => {
      const updated = { ...prev, [quarter]: val };
      const sum = ['q1','q2','q3','q4'].reduce((s, k) => s + (parseInt(String(updated[k]).replace(/[^0-9]/g, '')) || 0), 0);
      if (sum > 0) updated.annual = String(sum);
      return updated;
    });
  };

  // Get goal for a specific quarter key like 'Q1' for a specific year
  // Uses the year column (preferred) with a fallback to start_date for legacy rows
  const getGoalForQuarter = (qLabel, yr = null) => {
    const period = qLabel.toLowerCase();
    const targetYear = yr != null ? yr : (selectedYear === 'all' ? null : selectedYear);
    if (targetYear == null) return null; // no quarter-goals in All Time view
    return goals.find(g => {
      if (g.period !== period) return false;
      const gy = g.year != null ? g.year : (g.start_date ? new Date(g.start_date).getFullYear() : null);
      return gy === targetYear;
    });
  };

  // Get annual goal for a specific year
  const getAnnualGoal = (yr) => {
    return goals.find(g => {
      if (g.period !== 'annual') return false;
      const gy = g.year != null ? g.year : (g.start_date ? new Date(g.start_date).getFullYear() : null);
      return gy === yr;
    });
  };
  const handleDrop = async (e, targetStage) => { e.preventDefault(); if (!draggedDeal || draggedDeal.stage === targetStage) { setDraggedDeal(null); return; } try { await api.patch(`/deals/${draggedDeal.id}/stage`, { stage: targetStage }); fetchPipeline(); fetchAllDeals(); } catch (e) { alert('Failed'); } setDraggedDeal(null); };
  const formatCurrency = (amt) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amt || 0);

  // Quarterly calculations
  const getQuarterFromDate = (dateStr) => {
    if (!dateStr) return null;
    // Parse date properly to avoid timezone issues
    const parts = dateStr.split('T')[0].split('-');
    const month = parseInt(parts[1], 10) - 1; // 0-indexed
    return Math.floor(month / 3) + 1;
  };

  const getYearFromDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('T')[0].split('-');
    return parseInt(parts[0], 10);
  };

  // Build quarterly aggregates for a specific year.
  // For the current year or future years: uses real deals from allDeals.
  // For past years (year < CURRENT_YEAR): uses historicals only. Historicals are stored
  // at quarter granularity and carry revenue + expenses directly.
  const getQuarterlyDataForYear = (yr) => {
    const quarters = {
      Q1: { revenue: 0, costs: 0, deals: [], historicals: [] },
      Q2: { revenue: 0, costs: 0, deals: [], historicals: [] },
      Q3: { revenue: 0, costs: 0, deals: [], historicals: [] },
      Q4: { revenue: 0, costs: 0, deals: [], historicals: [] },
    };

    if (yr >= CURRENT_YEAR) {
      // Current or future year — use real deals
      allDeals.forEach(deal => {
        const dateStr = deal.campaign_start_date || deal.expected_close_date;
        if (!dateStr) return;
        const year = getYearFromDate(dateStr);
        if (year !== yr) return;
        const q = getQuarterFromDate(dateStr);
        if (!q) return;

        const qKey = `Q${q}`;
        const dealValue = parseFloat(deal.total_deal_value) || 0;
        const stagePercent = (deal.stage_percent || stages[deal.stage]?.percent || 0) / 100;
        const costs = parseFloat(deal.total_hard_costs) || 0;

        if (deal.stage === 'closed_lost') return;

        const value = quarterlyWeighted ? dealValue * stagePercent : dealValue;
        const costValue = quarterlyWeighted ? costs * stagePercent : costs;

        quarters[qKey].revenue += value;
        quarters[qKey].costs += costValue;
        quarters[qKey].deals.push({ ...deal, weightedValue: value });
      });
    } else {
      // Past year — use historicals
      historicals.filter(h => h.year === yr).forEach(h => {
        const qKey = `Q${h.quarter}`;
        if (!quarters[qKey]) return;
        quarters[qKey].revenue += parseFloat(h.revenue) || 0;
        quarters[qKey].costs   += parseFloat(h.expenses) || 0;
        quarters[qKey].historicals.push(h);
      });
    }

    return quarters;
  };

  // Compatibility shim — getQuarterlyData() returns for the currently selected year
  const getQuarterlyData = () => {
    const yr = selectedYear === 'all' ? CURRENT_YEAR : selectedYear;
    return getQuarterlyDataForYear(yr);
  };

  const quarterlyData = getQuarterlyData();

  // Years available in the year dropdown:
  //   Fixed range: 2024 → CURRENT_YEAR + 3 (future-proof for planning)
  //   Plus any year with real deals or historicals (in case an old deal predates 2024)
  const yearSet = new Set();
  for (let y = 2024; y <= CURRENT_YEAR + 3; y++) yearSet.add(y);
  allDeals.forEach(d => {
    const y = getYearFromDate(d.campaign_start_date || d.expected_close_date || d.created_at);
    if (y) yearSet.add(y);
  });
  historicals.forEach(h => { if (h.year) yearSet.add(h.year); });
  const years = Array.from(yearSet).sort((a, b) => b - a);

  // Convenience flags for the quarterly view
  const isAllTime = selectedYear === 'all';
  const isPastYear = !isAllTime && selectedYear < CURRENT_YEAR;
  const canSetTargets = !isAllTime && selectedYear >= CURRENT_YEAR;
  const canAddHistoricals = isAllTime || isPastYear;

  // Roll-up totals per year (used for All Time card row + annual vs target labels)
  const getYearTotals = (yr) => {
    const qd = getQuarterlyDataForYear(yr);
    const revenue = ['Q1','Q2','Q3','Q4'].reduce((s,q) => s + qd[q].revenue, 0);
    const costs   = ['Q1','Q2','Q3','Q4'].reduce((s,q) => s + qd[q].costs, 0);
    const dealCount = ['Q1','Q2','Q3','Q4'].reduce((s,q) => s + qd[q].deals.length + qd[q].historicals.length, 0);
    return { revenue, costs, net: revenue - costs, dealCount };
  };

  // Years that actually have data (for All Time card strip)
  const yearsWithData = Array.from(new Set([
    ...allDeals.map(d => getYearFromDate(d.campaign_start_date || d.expected_close_date || d.created_at)).filter(Boolean),
    ...historicals.map(h => h.year),
  ])).sort((a, b) => a - b);

  if (loading) return <div className="p-8 text-zinc-400">Loading...</div>;

  return (
    <div className="p-8 min-w-0 overflow-hidden">
      <style>{`.kanban-grab::-webkit-scrollbar { display: none; }`}</style>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Pipeline</h1>
          <p className="text-zinc-400 mt-1">{totals.total_deals} deals • {formatCurrency(totals.total_value)} total • {formatCurrency(totals.weighted_value)} weighted</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex bg-zinc-800 rounded-lg p-1">
            <button onClick={() => setViewMode('kanban')} className={`px-3 py-1 rounded text-sm font-medium ${viewMode === 'kanban' ? 'bg-nextide-turquoise text-black' : 'text-zinc-400'}`}>Kanban</button>
            <button onClick={() => setViewMode('list')} className={`px-3 py-1 rounded text-sm font-medium ${viewMode === 'list' ? 'bg-nextide-turquoise text-black' : 'text-zinc-400'}`}>List</button>
            <button onClick={() => setViewMode('quarterly')} className={`px-3 py-1 rounded text-sm font-medium ${viewMode === 'quarterly' ? 'bg-nextide-turquoise text-black' : 'text-zinc-400'}`}>Quarterly</button>
          </div>
          <button
            onClick={openTargetModal}
            disabled={viewMode === 'quarterly' && !canSetTargets}
            title={viewMode === 'quarterly' && !canSetTargets ? 'Targets can only be set for current or future years' : ''}
            className={`px-4 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${viewMode === 'quarterly' && !canSetTargets ? 'bg-zinc-900 border border-zinc-800 text-zinc-600 cursor-not-allowed' : 'bg-zinc-800 border border-zinc-700 text-white hover:border-zinc-600'}`}
          >
            🎯 Sales Target
          </button>
          {viewMode === 'quarterly' && canAddHistoricals && (
            <button
              onClick={() => setShowHistoricalModal(true)}
              className="bg-zinc-800 border border-zinc-700 text-white px-4 py-3 rounded-lg font-medium hover:border-zinc-600 transition-colors flex items-center gap-2"
            >
              + Historical Deal
            </button>
          )}
          <button onClick={() => setShowCreate(true)} className="bg-nextide-turquoise text-black px-6 py-3 rounded-lg font-semibold">+ New Deal</button>
        </div>
      </div>

      {viewMode === 'quarterly' && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <select
                value={selectedYear}
                onChange={e => setSelectedYear(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
              >
                <option value="all">All Time</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              {!isPastYear && !isAllTime && (
                <div className="flex bg-zinc-800 rounded-lg p-1">
                  <button onClick={() => setQuarterlyWeighted(true)} className={`px-3 py-1 rounded text-sm font-medium ${quarterlyWeighted ? 'bg-nextide-turquoise text-black' : 'text-zinc-400'}`}>Weighted</button>
                  <button onClick={() => setQuarterlyWeighted(false)} className={`px-3 py-1 rounded text-sm font-medium ${!quarterlyWeighted ? 'bg-nextide-turquoise text-black' : 'text-zinc-400'}`}>Unweighted</button>
                </div>
              )}
            </div>
            <div className="text-sm text-zinc-400">
              {isAllTime ? 'Year-over-year view' : isPastYear ? 'Historical — actuals from historical deals' : (quarterlyWeighted ? 'Values weighted by stage probability' : 'Full deal values shown')}
            </div>
          </div>

          {/* ============================================================
              ALL TIME VIEW — one card per year, yearly bar chart
              ============================================================ */}
          {isAllTime && (
            <>
              {yearsWithData.length === 0 ? (
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
                  <div className="text-zinc-400 mb-2">No deals or historical data yet</div>
                  <div className="text-zinc-600 text-sm">Create a deal or add historical deals to see year-over-year.</div>
                </div>
              ) : (
                <>
                  <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: `repeat(${Math.min(yearsWithData.length, 6)}, minmax(0, 1fr))` }}>
                    {yearsWithData.map(yr => {
                      const yt = getYearTotals(yr);
                      const annualGoal = getAnnualGoal(yr);
                      const target = annualGoal ? annualGoal.target_amount : 0;
                      const pct = target > 0 ? Math.min(100, Math.round((yt.revenue / target) * 100)) : 0;
                      const isCurrent = yr === CURRENT_YEAR;
                      return (
                        <div key={yr} className={`bg-zinc-900 border rounded-xl p-5 ${isCurrent ? 'border-nextide-turquoise/30' : 'border-zinc-800'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold text-white">{yr}</h3>
                            {isCurrent && <span className="text-[10px] bg-nextide-turquoise/10 text-nextide-turquoise px-1.5 py-0.5 rounded font-medium">CURRENT</span>}
                          </div>
                          <div className="space-y-2.5">
                            <div>
                              <div className="text-zinc-500 text-xs">Revenue</div>
                              <div className="text-xl font-bold text-nextide-turquoise">{formatCurrency(yt.revenue)}</div>
                            </div>
                            {target > 0 && (
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-zinc-500 text-[10px]">vs Target</span>
                                  <span className={`text-[10px] font-medium ${pct >= 100 ? 'text-green-400' : pct >= 70 ? 'text-amber-400' : 'text-zinc-400'}`}>{pct}%</span>
                                </div>
                                <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${pct >= 100 ? 'bg-green-500' : pct >= 70 ? 'bg-amber-500' : 'bg-nextide-turquoise'}`} style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            )}
                            <div>
                              <div className="text-zinc-500 text-xs">Expenses</div>
                              <div className="text-sm font-semibold text-red-400">-{formatCurrency(yt.costs)}</div>
                            </div>
                            <hr className="border-zinc-800" />
                            <div>
                              <div className="text-zinc-500 text-xs">Net</div>
                              <div className={`text-base font-bold ${yt.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCurrency(yt.net)}</div>
                            </div>
                            <div className="text-zinc-500 text-[10px]">{yt.dealCount} {yt.dealCount === 1 ? 'deal' : 'deals'}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Year-over-year bar chart */}
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                    <h3 className="text-white font-semibold mb-4">Year over Year</h3>
                    <div className="flex items-end gap-8 h-48 relative">
                      {(() => {
                        const rows = yearsWithData.map(yr => ({ yr, ...getYearTotals(yr) }));
                        const maxVal = Math.max(...rows.map(r => r.revenue), 1);
                        return rows.map(r => {
                          const revenueHeight = (r.revenue / maxVal) * 100;
                          const costsHeight = (r.costs / maxVal) * 100;
                          return (
                            <div key={r.yr} className="flex-1 flex flex-col items-center">
                              <div className="flex items-end gap-2 h-40 w-full justify-center">
                                <div className="w-8 bg-nextide-turquoise/80 rounded-t" style={{ height: `${revenueHeight}%` }} title={`Revenue: ${formatCurrency(r.revenue)}`} />
                                <div className="w-8 bg-red-500/80 rounded-t" style={{ height: `${costsHeight}%` }} title={`Costs: ${formatCurrency(r.costs)}`} />
                              </div>
                              <div className="text-zinc-400 text-sm mt-2">{r.yr}</div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                    <div className="flex justify-center gap-8 mt-4">
                      <div className="flex items-center gap-2"><div className="w-4 h-4 bg-nextide-turquoise/80 rounded" /><span className="text-zinc-400 text-sm">Revenue</span></div>
                      <div className="flex items-center gap-2"><div className="w-4 h-4 bg-red-500/80 rounded" /><span className="text-zinc-400 text-sm">Expenses</span></div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* ============================================================
              SPECIFIC YEAR VIEW — quarterly cards + chart + deals table
              (current, future, or past — past years populate from historicals)
              ============================================================ */}
          {!isAllTime && (
            <>
              <div className="grid grid-cols-4 gap-4 mb-6">
                {['Q1', 'Q2', 'Q3', 'Q4'].map(q => {
                  const goal = getGoalForQuarter(q);
                  const target = goal ? goal.target_amount : 0;
                  const rev = quarterlyData[q].revenue;
                  const pct = target > 0 ? Math.min(100, Math.round((rev / target) * 100)) : 0;
                  const now = new Date();
                  const currentQ = Math.ceil((now.getMonth() + 1) / 3);
                  const isCurrent = selectedYear === now.getFullYear() && parseInt(q.replace('Q', '')) === currentQ;
                  const dealCount = quarterlyData[q].deals.length + quarterlyData[q].historicals.length;
                  return (
                    <div key={q} className={`bg-zinc-900 border rounded-xl p-6 ${isCurrent ? 'border-nextide-turquoise/30' : 'border-zinc-800'}`}>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-white">{q} {selectedYear}</h3>
                        {isCurrent && <span className="text-[10px] bg-nextide-turquoise/10 text-nextide-turquoise px-1.5 py-0.5 rounded font-medium">ACTIVE</span>}
                        {isPastYear && <span className="text-[10px] bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded font-medium">HISTORICAL</span>}
                      </div>
                      <div className="space-y-3">
                        <div>
                          <div className="text-zinc-500 text-sm">Revenue</div>
                          <div className="text-2xl font-bold text-nextide-turquoise">{formatCurrency(rev)}</div>
                        </div>
                        {target > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-zinc-500 text-xs">vs Target</span>
                              <span className={`text-xs font-medium ${pct >= 100 ? 'text-green-400' : pct >= 70 ? 'text-amber-400' : 'text-zinc-400'}`}>{pct}% of {formatCurrency(target)}</span>
                            </div>
                            <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-green-500' : pct >= 70 ? 'bg-amber-500' : 'bg-nextide-turquoise'}`} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        )}
                        <div>
                          <div className="text-zinc-500 text-sm">Campaign Expenses</div>
                          <div className="text-xl font-semibold text-red-400">-{formatCurrency(quarterlyData[q].costs)}</div>
                        </div>
                        <hr className="border-zinc-700" />
                        <div>
                          <div className="text-zinc-500 text-sm">Net</div>
                          <div className={`text-xl font-bold ${rev - quarterlyData[q].costs >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {formatCurrency(rev - quarterlyData[q].costs)}
                          </div>
                        </div>
                        <div className="text-zinc-500 text-xs">{dealCount} {dealCount === 1 ? 'deal' : 'deals'}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Quarterly bar chart */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-4">{selectedYear} Pipeline Overview</h3>
                <div className="flex items-end gap-8 h-48 relative">
                  {(() => {
                    const allTargets = ['Q1', 'Q2', 'Q3', 'Q4'].map(q => { const g = getGoalForQuarter(q); return g ? g.target_amount : 0; });
                    const maxTarget = Math.max(...allTargets);
                    const maxRevenue = Math.max(...Object.values(quarterlyData).map(d => d.revenue));
                    const maxVal = Math.max(maxTarget, maxRevenue) || 1;
                    return ['Q1', 'Q2', 'Q3', 'Q4'].map(q => {
                      const revenueHeight = (quarterlyData[q].revenue / maxVal) * 100;
                      const costsHeight = (quarterlyData[q].costs / maxVal) * 100;
                      const goal = getGoalForQuarter(q);
                      const targetHeight = goal ? (goal.target_amount / maxVal) * 100 : 0;
                      return (
                        <div key={q} className="flex-1 flex flex-col items-center">
                          <div className="flex items-end gap-2 h-40 w-full justify-center relative">
                            {targetHeight > 0 && (
                              <div className="absolute left-0 right-0 border-t-2 border-dashed border-amber-500/50" style={{ bottom: `${targetHeight}%` }} title={`Target: ${formatCurrency(goal.target_amount)}`} />
                            )}
                            <div className="w-8 bg-nextide-turquoise/80 rounded-t relative z-10" style={{ height: `${revenueHeight}%` }} title={`Revenue: ${formatCurrency(quarterlyData[q].revenue)}`} />
                            <div className="w-8 bg-red-500/80 rounded-t relative z-10" style={{ height: `${costsHeight}%` }} title={`Costs: ${formatCurrency(quarterlyData[q].costs)}`} />
                          </div>
                          <div className="text-zinc-400 text-sm mt-2">{q}</div>
                        </div>
                      );
                    });
                  })()}
                </div>
                <div className="flex justify-center gap-8 mt-4">
                  <div className="flex items-center gap-2"><div className="w-4 h-4 bg-nextide-turquoise/80 rounded" /><span className="text-zinc-400 text-sm">Revenue</span></div>
                  <div className="flex items-center gap-2"><div className="w-4 h-4 bg-red-500/80 rounded" /><span className="text-zinc-400 text-sm">Expenses</span></div>
                  {goals.some(g => (g.year != null ? g.year : new Date(g.start_date).getFullYear()) === selectedYear) && (
                    <div className="flex items-center gap-2"><div className="w-4 h-0 border-t-2 border-dashed border-amber-500/50" style={{ width: '16px' }} /><span className="text-zinc-400 text-sm">Target</span></div>
                  )}
                </div>
              </div>

              {/* Deals by quarter — current/future year uses real deals, past year uses historicals */}
              {!isPastYear ? (
                <div className="mt-6 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead><tr className="border-b border-zinc-800"><th className="text-left p-4 text-zinc-400 text-sm">Quarter</th><th className="text-left p-4 text-zinc-400 text-sm">Deal</th><th className="text-left p-4 text-zinc-400 text-sm">Stage</th><th className="text-left p-4 text-zinc-400 text-sm">Value</th><th className="text-left p-4 text-zinc-400 text-sm">{quarterlyWeighted ? 'Weighted' : 'Costs'}</th></tr></thead>
                    <tbody>
                      {['Q1', 'Q2', 'Q3', 'Q4'].flatMap(q => quarterlyData[q].deals.map((d, i) => (
                        <tr key={`${q}-${d.id}`} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                          <td className="p-4 text-zinc-400">{i === 0 ? q : ''}</td>
                          <td className="p-4"><Link to={`/pipeline/${d.id}`} className="text-white hover:text-nextide-turquoise">{d.name}</Link></td>
                          <td className="p-4"><span className={`px-2 py-1 rounded text-xs ${d.stage === 'closed_won' ? 'bg-green-500/20 text-green-400' : 'bg-zinc-800 text-zinc-300'}`}>{stages[d.stage]?.label || d.stage}</span></td>
                          <td className="p-4 text-nextide-turquoise">{formatCurrency(d.total_deal_value)}</td>
                          <td className="p-4 text-zinc-300">{quarterlyWeighted ? formatCurrency(d.weightedValue) : formatCurrency(d.total_hard_costs)}</td>
                        </tr>
                      )))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mt-6 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
                    <div className="text-zinc-300 text-sm font-medium">Historical Deals — {selectedYear}</div>
                    <div className="text-zinc-500 text-xs">{historicals.filter(h => h.year === selectedYear).length} deals</div>
                  </div>
                  <table className="w-full">
                    <thead><tr className="border-b border-zinc-800"><th className="text-left p-4 text-zinc-400 text-sm">Quarter</th><th className="text-left p-4 text-zinc-400 text-sm">Deal</th><th className="text-left p-4 text-zinc-400 text-sm">Revenue</th><th className="text-left p-4 text-zinc-400 text-sm">Expenses</th><th className="text-left p-4 text-zinc-400 text-sm">Net</th><th className="p-4 w-12"></th></tr></thead>
                    <tbody>
                      {['Q1', 'Q2', 'Q3', 'Q4'].flatMap(q => quarterlyData[q].historicals.map((h, i) => {
                        const rev = parseFloat(h.revenue) || 0;
                        const exp = parseFloat(h.expenses) || 0;
                        return (
                          <tr key={h.id} className="border-b border-zinc-800 hover:bg-zinc-800/50 group">
                            <td className="p-4 text-zinc-400">{i === 0 ? q : ''}</td>
                            <td className="p-4 text-white">{h.name}</td>
                            <td className="p-4 text-nextide-turquoise">{formatCurrency(rev)}</td>
                            <td className="p-4 text-red-400">-{formatCurrency(exp)}</td>
                            <td className={`p-4 font-medium ${rev - exp >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCurrency(rev - exp)}</td>
                            <td className="p-4">
                              <button
                                onClick={() => deleteHistorical(h.id)}
                                className="text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity text-sm"
                                title="Delete historical deal"
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        );
                      }))}
                      {historicals.filter(h => h.year === selectedYear).length === 0 && (
                        <tr><td colSpan={6} className="p-8 text-center text-zinc-500 text-sm">No historical deals for {selectedYear} yet. Click "+ Historical Deal" to add one.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {viewMode === 'kanban' && (
        <div
          ref={kanbanRef}
          onMouseDown={onMouseDown}
          className="kanban-grab flex gap-4 overflow-x-auto pb-4 scrollbar-thin"
          style={{ cursor: 'grab', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {stageOrder.map(stageKey => {
            const s = pipeline[stageKey]; if (!s) return null;
            return (
              <div key={stageKey} className="flex-shrink-0 w-72 bg-zinc-900 rounded-xl border border-zinc-800" onDragOver={e => e.preventDefault()} onDrop={e => handleDrop(e, stageKey)}>
                <div className={`p-4 border-b border-zinc-800 ${stageKey === 'closed_won' ? 'bg-green-500/10' : stageKey === 'closed_lost' ? 'bg-red-500/10' : ''}`}>
                  <div className="flex justify-between mb-1"><h3 className={`font-semibold text-sm ${stageKey === 'closed_won' ? 'text-green-400' : stageKey === 'closed_lost' ? 'text-red-400' : 'text-white'}`}>{s.label}</h3><span className="text-xs bg-zinc-800 text-zinc-400 px-2 py-1 rounded">{s.percent}%</span></div>
                  <div className="flex justify-between text-xs text-zinc-500"><span>{s.deals.length} deals</span><span>{formatCurrency(s.total_value)}</span></div>
                </div>
                <div className="p-2 space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto">
                  {s.deals.map(deal => (
                    <Link key={deal.id} to={`/pipeline/${deal.id}`} draggable onDragStart={() => setDraggedDeal(deal)} className="block p-3 bg-zinc-800 rounded-lg hover:bg-zinc-700 cursor-grab">
                      <div className="font-medium text-white text-sm truncate">{deal.name}</div>
                      {deal.account && <div className="text-zinc-500 text-xs truncate">{deal.account}</div>}
                      <div className="flex justify-between mt-1"><span className="text-nextide-turquoise font-semibold text-sm">{formatCurrency(deal.total_deal_value)}</span>{deal.vertical && <span className="text-xs bg-zinc-700 text-zinc-400 px-2 rounded">{deal.vertical}</span>}</div>
                      {deal.net_revenue > 0 && <div className="text-green-400 text-xs mt-1">Net: {formatCurrency(deal.net_revenue)}</div>}
                    </Link>
                  ))}
                  {s.deals.length === 0 && <div className="text-zinc-600 text-sm text-center py-4">No deals</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {viewMode === 'list' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead><tr className="border-b border-zinc-800"><th className="text-left p-4 text-zinc-400 text-sm">Deal</th><th className="text-left p-4 text-zinc-400 text-sm">Account</th><th className="text-left p-4 text-zinc-400 text-sm">Stage</th><th className="text-left p-4 text-zinc-400 text-sm">Value</th><th className="text-left p-4 text-zinc-400 text-sm">Net Revenue</th><th className="text-left p-4 text-zinc-400 text-sm">Margin</th></tr></thead>
            <tbody>
              {stageOrder.flatMap(sk => (pipeline[sk]?.deals || []).map(d => (
                <tr key={d.id} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                  <td className="p-4"><Link to={`/pipeline/${d.id}`} className="text-white hover:text-nextide-turquoise">{d.name}</Link></td>
                  <td className="p-4 text-zinc-300">{d.account || '-'}</td>
                  <td className="p-4"><span className={`px-2 py-1 rounded text-xs ${d.stage === 'closed_won' ? 'bg-green-500/20 text-green-400' : d.stage === 'closed_lost' ? 'bg-red-500/20 text-red-400' : 'bg-zinc-800 text-zinc-300'}`}>{stages[d.stage]?.label}</span></td>
                  <td className="p-4 text-nextide-turquoise font-medium">{formatCurrency(d.total_deal_value)}</td>
                  <td className="p-4 text-green-400">{formatCurrency(d.net_revenue)}</td>
                  <td className="p-4 text-zinc-300">{d.gross_margin_percent || 0}%</td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      )}

      <CreateDealModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => { fetchPipeline(); fetchAllDeals(); }}
      />
      {/* SALES TARGET MODAL                                           */}
      {/* ============================================================ */}
      {showTargetModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowTargetModal(false)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white">🎯 Sales Targets</h2>
                <p className="text-zinc-500 text-sm mt-0.5">Set your quarterly and annual revenue goals</p>
              </div>
              <button onClick={() => setShowTargetModal(false)} className="text-zinc-500 hover:text-white text-xl">✕</button>
            </div>

            <div className="p-6 space-y-5">
              {/* Year selector */}
              <div className="flex items-center gap-3">
                <span className="text-zinc-400 text-sm">Year</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setGoalYear(prev => prev - 1)} className="w-8 h-8 rounded bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors flex items-center justify-center text-sm">←</button>
                  <span className="text-white font-semibold text-sm w-12 text-center">{goalYear}</span>
                  <button onClick={() => setGoalYear(prev => prev + 1)} className="w-8 h-8 rounded bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors flex items-center justify-center text-sm">→</button>
                </div>
              </div>

              {/* Annual */}
              <div>
                <label className="block text-zinc-400 text-sm mb-1.5 font-medium">Annual Target</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <input
                    type="text"
                    value={fmtGoalNum(goalsForm.annual)}
                    onChange={e => handleAnnualGoalChange(e.target.value)}
                    placeholder="e.g. 2,000,000"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-7 pr-4 py-2.5 text-white focus:outline-none focus:border-nextide-turquoise text-sm"
                  />
                </div>
                <p className="text-zinc-600 text-xs mt-1">If quarters are empty, auto-splits evenly across Q1–Q4</p>
              </div>

              {/* Quarters */}
              <div>
                <label className="block text-zinc-400 text-sm mb-1.5 font-medium">Quarterly Breakdown</label>
                <div className="grid grid-cols-4 gap-3">
                  {['q1', 'q2', 'q3', 'q4'].map((q, i) => {
                    const now = new Date();
                    const currentQ = Math.ceil((now.getMonth() + 1) / 3);
                    const isCurrent = goalYear === now.getFullYear() && (i + 1) === currentQ;
                    return (
                      <div key={q}>
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-xs text-zinc-500">Q{i + 1}</span>
                          {isCurrent && <span className="text-[10px] bg-nextide-turquoise/10 text-nextide-turquoise px-1 rounded">now</span>}
                        </div>
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
                          <input
                            type="text"
                            value={fmtGoalNum(goalsForm[q])}
                            onChange={e => handleQuarterGoalChange(q, e.target.value)}
                            placeholder="0"
                            className={`w-full bg-zinc-800 border rounded-lg pl-6 pr-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise ${isCurrent ? 'border-nextide-turquoise/30' : 'border-zinc-700'}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-zinc-600 text-xs mt-1.5">Editing quarters auto-sums to annual</p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-zinc-800 flex justify-end gap-3">
              <button onClick={() => setShowTargetModal(false)} className="px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 text-sm">Cancel</button>
              <button onClick={saveGoals} disabled={savingGoals} className="px-5 py-2 bg-nextide-turquoise text-black rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 text-sm">
                {savingGoals ? 'Saving...' : 'Save Targets'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* HISTORICAL DEAL MODAL                                        */}
      {/* ============================================================ */}
      {showHistoricalModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowHistoricalModal(false)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white">+ Historical Deal</h2>
                <p className="text-zinc-500 text-sm mt-0.5">Backfill a past deal so it appears in your year-over-year view.</p>
              </div>
              <button onClick={() => setShowHistoricalModal(false)} className="text-zinc-500 hover:text-white text-xl">✕</button>
            </div>

            <div className="p-6 space-y-4">
              {/* Deal Name */}
              <div>
                <label className="block text-zinc-400 text-sm mb-1.5 font-medium">Deal Name</label>
                <input
                  type="text"
                  value={histForm.name}
                  onChange={e => setHistForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. State Farm Q3 Campaign"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-nextide-turquoise text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Revenue */}
                <div>
                  <label className="block text-zinc-400 text-sm mb-1.5 font-medium">Revenue</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                    <input
                      type="text"
                      value={fmtGoalNum(histForm.revenue)}
                      onChange={e => setHistForm(p => ({ ...p, revenue: e.target.value }))}
                      placeholder="150,000"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-7 pr-3 py-2.5 text-white focus:outline-none focus:border-nextide-turquoise text-sm"
                    />
                  </div>
                </div>

                {/* Expenses */}
                <div>
                  <label className="block text-zinc-400 text-sm mb-1.5 font-medium">Campaign Expense</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                    <input
                      type="text"
                      value={fmtGoalNum(histForm.expenses)}
                      onChange={e => setHistForm(p => ({ ...p, expenses: e.target.value }))}
                      placeholder="45,000"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-7 pr-3 py-2.5 text-white focus:outline-none focus:border-nextide-turquoise text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Year */}
                <div>
                  <label className="block text-zinc-400 text-sm mb-1.5 font-medium">Year</label>
                  <select
                    value={histForm.year}
                    onChange={e => setHistForm(p => ({ ...p, year: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-nextide-turquoise text-sm"
                  >
                    {(() => {
                      // Allow any year from 2015 up through CURRENT_YEAR - 1 (historicals are strictly past)
                      const opts = [];
                      for (let y = CURRENT_YEAR - 1; y >= 2015; y--) opts.push(y);
                      return opts.map(y => <option key={y} value={y}>{y}</option>);
                    })()}
                  </select>
                </div>

                {/* Quarter */}
                <div>
                  <label className="block text-zinc-400 text-sm mb-1.5 font-medium">Quarter</label>
                  <div className="grid grid-cols-4 gap-1.5">
                    {['1', '2', '3', '4'].map(q => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => setHistForm(p => ({ ...p, quarter: q }))}
                        className={`py-2.5 rounded-lg text-sm font-medium transition-all ${
                          histForm.quarter === q
                            ? 'bg-nextide-turquoise text-black'
                            : 'bg-zinc-800 border border-zinc-700 text-zinc-400 hover:border-zinc-600'
                        }`}
                      >
                        Q{q}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="text-zinc-600 text-xs pt-1">
                These appear only in past-year and All Time views — they don't affect your active pipeline.
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-zinc-800 flex justify-end gap-3">
              <button onClick={() => setShowHistoricalModal(false)} className="px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 text-sm">Cancel</button>
              <button onClick={saveHistorical} disabled={savingHistorical} className="px-5 py-2 bg-nextide-turquoise text-black rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 text-sm">
                {savingHistorical ? 'Saving...' : 'Add Historical Deal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pipeline;
